import {API_URL} from "./api/index";

const AVATARS = ["🦊", "🐼", "🦉", "🐨", "🦁", "🐸", "🐻", "🐯", "🦄"];
const AVATAR_COLORS = ["#0F6D73", "#F2C14E", "#4CAF50", "#3F51B5", "#9C27B0", "#FF9800"];

// Lookup animal → emoji + couleur (même liste que le backend)
const ANIMAL_INFO = {
    "Renard":         { emoji: "🦊", couleur: "#D4622A" },
    "Castor":         { emoji: "🦫", couleur: "#8B5E3C" },
    "Loutre":         { emoji: "🦦", couleur: "#4A8DB8" },
    "Hérisson":       { emoji: "🦔", couleur: "#7D6B5A" },
    "Panda":          { emoji: "🐼", couleur: "#4A4A4A" },
    "Pingouin":       { emoji: "🐧", couleur: "#2C4A7C" },
    "Koala":          { emoji: "🐨", couleur: "#7A9E7E" },
    "Lapin":          { emoji: "🐰", couleur: "#CF8EBE" },
    "Écureuil":       { emoji: "🐿️", couleur: "#C47A2B" },
    "Chouette":       { emoji: "🦉", couleur: "#7B5E3E" },
    "Flamant":        { emoji: "🦩", couleur: "#C85080" },
    "Canard":         { emoji: "🦆", couleur: "#5A8F45" },
    "Lama":           { emoji: "🦙", couleur: "#B09060" },
    "Toucan":         { emoji: "🦜", couleur: "#2A7F62" },
    "Paresseux":      { emoji: "🦥", couleur: "#8FA870" },
    "Narval":         { emoji: "🐳", couleur: "#3A78A8" },
    "Axolotl":        { emoji: "🦎", couleur: "#D46A90" },
    "Capybara":       { emoji: "🐹", couleur: "#A87B52" },
    "Fennec":         { emoji: "🦊", couleur: "#D4A040" },
    "Manchot":        { emoji: "🐧", couleur: "#3D5A80" },
    "Papillon":       { emoji: "🦋", couleur: "#B06AC9" },
    "Coccinelle":     { emoji: "🐞", couleur: "#D6402C" },
    "Fourmi":         { emoji: "🐜", couleur: "#3D3128" },
    "Grillon":        { emoji: "🦗", couleur: "#4F7A3D" },
    "Sauterelle":     { emoji: "🦗", couleur: "#6FA857" },
    "Abeille":        { emoji: "🐝", couleur: "#E8A93D" },
    "Chenille":       { emoji: "🐛", couleur: "#7CB342" },
    "Scarabée":       { emoji: "🪲", couleur: "#4A6B3A" },
    "Poisson-clown":  { emoji: "🐠", couleur: "#E8813D" },
    "Requin":         { emoji: "🦈", couleur: "#4A6B7A" },
    "Dauphin":        { emoji: "🐬", couleur: "#4AA8C4" },
    "Baleine":        { emoji: "🐋", couleur: "#3A5A7A" },
    "Poulpe":         { emoji: "🐙", couleur: "#B05A9A" },
    "Calamar":        { emoji: "🦑", couleur: "#7A4A9A" },
    "Crabe":          { emoji: "🦀", couleur: "#C4502A" },
    "Homard":         { emoji: "🦞", couleur: "#B03A2A" },
    "Crevette":       { emoji: "🦐", couleur: "#E88A6A" },
    "Méduse":         { emoji: "🪼", couleur: "#C98AC4" },
    "Tortue de mer":  { emoji: "🐢", couleur: "#4A8F5A" },
    "Otarie":         { emoji: "🦭", couleur: "#6B5A4A" },
    "Étoile de mer":  { emoji: "⭐", couleur: "#E8C547" },
    "Hippocampe":     { emoji: "🐴", couleur: "#C4703D" },
    "Poisson-globe":  { emoji: "🐡", couleur: "#D4A03D" },
    "Raie":           { emoji: "🐟", couleur: "#7A8FA8" },
    "Lion":           { emoji: "🦁", couleur: "#C98A2B" },
    "Grenouille":     { emoji: "🐸", couleur: "#5BA83C" },
    "Ours":           { emoji: "🐻", couleur: "#6B4A32" },
    "Tigre":          { emoji: "🐯", couleur: "#E58A1F" },
    "Licorne":        { emoji: "🦄", couleur: "#9F8AE0" },
};

// Noms d'animaux triés du plus long au plus court, calculé une seule fois (les noms à plusieurs mots d'abord)
const ANIMAL_NAMES_BY_LENGTH = Object.keys(ANIMAL_INFO).sort((a, b) => b.length - a.length);

// Illustration du badge : /animaux/<nom slugifié>.webp (ourco-web/public/animaux)
const animalImageUrl = (animal) => `${process.env.PUBLIC_URL || ""}/animaux/${slugify(animal)}.webp`;

// Toutes les illustrations d'animaux (bandeau défilant des rubriques libres de la landing).
export const ANIMAL_IMAGES = Object.keys(ANIMAL_INFO).map(name => ({name, image: animalImageUrl(name)}));

// Retrouve l'avatar du vrai animal si le pseudo suit le format Animal + Adjectif (reconnait aussi les noms d'animal à plusieurs mots)
function lookupAnimalAvatar(name) {
    if (!name) return null;
    const animal = ANIMAL_NAMES_BY_LENGTH
        .find(nom => name === nom || name.startsWith(nom + " "));
    const info = animal ? ANIMAL_INFO[animal] : null;
    return info ? { avatar: info.emoji, color: info.couleur, image: animalImageUrl(animal) } : null;
}

/**
 * Retourne { emoji, couleur } pour un pseudo, partout où il est affiché (bandeau, posts non anonymes).
 * Utilise le vrai animal choisi à l'inscription quand le pseudo le permet, sinon un avatar générique déterministe.
 */
export function generateAvatar(name) {
    name = name || "Agent";
    const animalAvatar = lookupAnimalAvatar(name);
    if (animalAvatar) return animalAvatar;
    return {
        avatar: AVATARS[name.length % AVATARS.length],
        color: AVATAR_COLORS[name.length % AVATAR_COLORS.length]
    };
}

/**
 * Retourne { emoji, couleur } pour un pseudonyme anonyme.
 * Les dérive toujours du nom de l'auteur, jamais des champs hérités.
 */
export function getAnonymousAvatar(post) {
    const animalAvatar = lookupAnimalAvatar(post.author);
    if (animalAvatar) return animalAvatar;
    return { avatar: "🔒", color: "#9CA3AF" };
}

export function validatePassword(pwd) {
    const minLength = pwd.length >= 8;
    const hasUpper = /[A-Z]/.test(pwd);
    const hasDigit = /[0-9]/.test(pwd);
    const hasSpecial = /[!@#$%^&*()\-_=+[\]{}|;:,.<>?]/.test(pwd);
    return { minLength, hasUpper, hasDigit, hasSpecial, valid: minLength && hasUpper && hasDigit && hasSpecial };
}

export function formatDate(dateString) {
    if (!dateString) return "Publication récente";
    return new Date(dateString).toLocaleString("fr-FR", {
        day: "2-digit", month: "2-digit", year: "numeric",
        hour: "2-digit", minute: "2-digit"
    });
}

export function buildInviteLink(token) {
    return `${window.location.origin}/invite/${token}`;
}

export function buildInviteEmailText(groupName, link) {
    return {
        subject: `Invitation à rejoindre "${groupName}" sur Paroles d'Agents`,
        body: `Bonjour,

Vous êtes invité(e) à rejoindre l'espace "${groupName}" sur Paroles d'Agents, la plateforme créée par le SAPPM pour les agents des services du Premier Ministre.

Partagez anonymement vos avis, propositions et ressentis sur les conditions de travail.

Votre anonymat est protégé :
- résultats anonymisés
- aucun résultat publié avant un seuil minimum de participation
- votre adresse professionnelle sert uniquement à vérifier l'accès

Rejoignez le collectif :
${link}

Le SAPPM vous écoute.`
    };
}

export function navigateTo(path) {
    window.history.pushState({}, "", path);
    window.dispatchEvent(new PopStateEvent("popstate"));
}

export function buildNewsImageUrl(relativePath) {
    if (!relativePath) return null;
    if (/^https?:\/\//i.test(relativePath)) return relativePath;
    return `${API_URL}${relativePath}`;
}

export function formatNewsDate(dateString) {
    if (!dateString) return "";
    return new Date(dateString).toLocaleDateString("fr-FR", {
        day: "2-digit", month: "long", year: "numeric",
    });
}

function moodPopupStorageKey(userId) {
    return `mood_popup_seen_${userId}`;
}

export function hasSeenMoodPopupToday(userId) {
    try {
        const todayKey = new Date().toISOString().slice(0, 10);
        return localStorage.getItem(moodPopupStorageKey(userId)) === todayKey;
    } catch {
        return true;
    }
}

export function markMoodPopupSeenToday(userId) {
    try {
        const todayKey = new Date().toISOString().slice(0, 10);
        localStorage.setItem(moodPopupStorageKey(userId), todayKey);
    } catch {
        // stockage indisponible (navigation privée…) : tant pis, la pop-up réapparaîtra
    }
}

// Adresse proposée à partir du nom d'une campagne : minuscules, sans accent, tirets.
export function slugify(name) {
    return name
        .normalize("NFD")
        .replace(/[̀-ͯ]/g, "")
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "");
}

const PARIS_FORMAT = new Intl.DateTimeFormat("sv-SE", {
    timeZone: "Europe/Paris", year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", hour12: false,
});

// Instant UTC → valeur d'un <input type="datetime-local"> exprimée en heure de Paris (« 2026-10-21T18:00 »).
export function isoToParisLocal(iso) {
    return PARIS_FORMAT.format(new Date(iso)).replace(" ", "T");
}

function parisWallClockMs(ms) {
    return Date.parse(`${isoToParisLocal(new Date(ms).toISOString())}:00Z`);
}

// Saisie en heure de Paris → instant UTC. Le navigateur de l'admin peut être dans un autre fuseau :
// on ne se fie donc pas à `new Date(local)`. Deux passes pour encaisser les changements d'heure.
export function parisLocalToIso(local) {
    if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(local || "")) return "";
    const target = Date.parse(`${local}:00Z`);
    let ms = target - (parisWallClockMs(target) - target);
    ms -= parisWallClockMs(ms) - target;
    return new Date(ms).toISOString();
}
